import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../AuthProvider';
import { useCloudSave } from '../../hooks/useCloudSave';
import { useQuota } from '../../hooks/useQuota';
import { CanvasMetadata } from '../../services/cloudSave/canvasMetadata';
import { detectFileFormat, importTreeFromFile } from '../../utils/treeImporter';
import { exportTreeToJSON } from '../../utils/treeExporter';
import { saveCanvasToCloudAsync, ParallelSaveOptions } from '../../utils/parallelSaveUtils';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';
import { trackEvent } from '../../utils/analytics';
import CloudFileBrowser from '../CloudFileBrowser';
import ConfirmationDialog from '../ConfirmationDialog';
import './UserTreeFiles.css';

interface UserTreeFilesProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserTreeFiles: React.FC<UserTreeFilesProps> = ({ isOpen, onClose }) => {
  const { user } = useAuthContext();
  const { loadCanvas, deleteCanvas, loadUserCanvases, loading: cloudLoading, userCanvases, error: cloudError } = useCloudSave();
  const { loadTree, showToast } = useWhiteboardStore();
  const { refreshQuota } = useQuota();
  const [selectedCanvas, setSelectedCanvas] = useState<CanvasMetadata | null>(null);
  const [loadingCanvas, setLoadingCanvas] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [canvasToDelete, setCanvasToDelete] = useState<CanvasMetadata | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadUserCanvases();
    }
  }, [isOpen, user, loadUserCanvases]);

  const handleYamlConversion = async (yamlContent: string, oldCanvasId: string, canvasName: string, description?: string, tags?: string[]) => {
    console.log('🔄 Starting YAML to JSON conversion for canvas:', canvasName, 'ID:', oldCanvasId);
    
    if (!user) {
      console.error('❌ YAML conversion failed: User not logged in');
      showToast('You must be logged in to convert files', 'error');
      return;
    }

    try {
      // 1. Load YAML content into state
      console.log('📖 Stage 1: Loading YAML content into state...');
      const state = await importTreeFromFile(yamlContent, oldCanvasId);
      console.log('✅ Stage 1 complete: YAML content loaded successfully. Notes count:', state.notes.length);
      
      // 2. Convert to JSON
      console.log('🔄 Stage 2: Converting state to JSON format...');
      const jsonContent = exportTreeToJSON(state);
      console.log('✅ Stage 2 complete: JSON conversion successful. Content length:', jsonContent.length, 'characters');
      
      // 3. Save as NEW canvas (no canvasId = new canvas)
      console.log('💾 Stage 3: Saving converted JSON as new canvas...');
      const options: ParallelSaveOptions = {
        userId: user.uid,
        canvasId: undefined, // This creates a new canvas
        canvasName: canvasName || state.canvasName || "Converted Canvas",
        description: description || state.description,
        tags: tags || state.tags
      };
      
      await saveCanvasToCloudAsync(options);
      console.log('✅ Stage 3 complete: New JSON canvas saved successfully');
      
      // 4. Delete old YAML canvas
      console.log('🗑️ Stage 4: Deleting old YAML canvas...');
      const deleteResult = await deleteCanvas(oldCanvasId);
      
      if (deleteResult.success) {
        console.log('✅ Stage 4 complete: Old YAML canvas deleted successfully');
        console.log('🎉 YAML to JSON conversion completed successfully for:', canvasName);
        showToast('Canvas converted to modern format!', 'success');
        trackEvent('yaml_to_json_conversion', { 
          old_canvas_id: oldCanvasId,
          canvas_name: canvasName 
        });
        // Refresh the canvas list to show the new JSON canvas
        loadUserCanvases();
      } else {
        console.warn('⚠️ Stage 4 partial: Old YAML canvas could not be deleted:', deleteResult.error);
        showToast('Canvas converted but old version could not be deleted', 'info');
      }
      
    } catch (error) {
      console.error('❌ YAML conversion failed at stage:', error);
      showToast('Conversion failed, but file loaded successfully', 'error');
    }
  };

  const handleCanvasLoad = async (canvas: CanvasMetadata) => {
    try {
      setLoadingCanvas(canvas.id);
      
      const result = await loadCanvas(canvas.id);
      if (result.success && result.content) {
        // Check if it's YAML and needs conversion
        const fileFormat = detectFileFormat(result.content);
        console.log('🔍 File format detected:', fileFormat, 'for canvas:', canvas.name);
        
        if (fileFormat === 'yaml') {
          console.log('🔄 YAML file detected, starting conversion process...');
          // Convert YAML to JSON
          await handleYamlConversion(
            result.content, 
            canvas.id, 
            canvas.name, 
            canvas.description, 
            canvas.tags
          );
        } else {
          console.log('✅ JSON file detected, no conversion needed');
        }
        
        // Load the canvas (works for both YAML and JSON)
        const treeState = await importTreeFromFile(result.content, canvas.id);
        // Ensure canvas name is set from cloud metadata
        treeState.canvasName = canvas.name;
        treeState.description = canvas.description;
        treeState.tags = canvas.tags;
        loadTree(treeState);
        trackEvent('thread_loaded_from_my_threads', { canvas_id: canvas.id, canvas_name: canvas.name });
        window.dispatchEvent(new CustomEvent('whiteboard-loaded', { 
          detail: { type: 'cloud', canvasName: canvas.name, canvasId: canvas.id } 
        }));
        showToast(`Loaded "${canvas.name}" successfully`, 'success');
        onClose();
      } else {
        showToast(result.error || 'Failed to load canvas', 'error');
      }
    } catch (error) {
      showToast('An unexpected error occurred', 'error');
    } finally {
      setLoadingCanvas(null);
    }
  };

  const handleCanvasDelete = async (canvas: CanvasMetadata, e: React.MouseEvent) => {
    e.stopPropagation();
    setCanvasToDelete(canvas);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (canvasToDelete) {
      setDeleteLoading(true);
      try {
        const result = await deleteCanvas(canvasToDelete.id);
        if (result.success) {
          // Refresh quota display after successful deletion
          await refreshQuota();
        }
        loadUserCanvases(); // Refresh list
        setShowDeleteConfirm(false);
        setCanvasToDelete(null);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setCanvasToDelete(null);
  };

  const isCanvasLoading = (canvasId: string) => loadingCanvas === canvasId;
  const isAnyLoading = cloudLoading || loadingCanvas !== null;

  if (!isOpen) return null;

  return (
    <div className="user-tree-files-overlay" onClick={onClose}>
      <div className="user-tree-files-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">My Canvases</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-content">
          <CloudFileBrowser
            onCanvasSelect={setSelectedCanvas}
            onCanvasDelete={handleCanvasDelete}
            selectedCanvas={selectedCanvas}
            userCanvases={userCanvases}
            loading={cloudLoading}
            error={cloudError}
            loadingCanvasId={loadingCanvas}
          />
        </div>
        <div className="modal-footer">
          <button className="secondary-button" onClick={onClose}>Close</button>
          <button 
            className="primary-button" 
            onClick={() => selectedCanvas && handleCanvasLoad(selectedCanvas)}
            disabled={!selectedCanvas || isAnyLoading}
          >
            {isAnyLoading ? (
              <span className="loading-button-content">
                <div className="button-spinner"></div>
                Loading...
              </span>
            ) : (
              'Load Selected'
            )}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title="Delete Canvas"
        message={`Are you sure you want to delete "${canvasToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={deleteLoading}
        loadingText="Deleting..."
      />
    </div>
  );
};

export default UserTreeFiles;
